import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { api } from "@/lib/api-client"
import type { PaginatedResponse, Deal, DealDetail, DealBoardColumn } from "@/lib/types"

interface DealFilters {
  search?: string
  status?: string
  phase_id?: string
  owner_id?: string
  deal_type_id?: string
  company_id?: string
  value_min?: string
  value_max?: string
  expected_close_from?: string
  expected_close_to?: string
  created_at_from?: string
  created_at_to?: string
  sort?: string
  cursor?: string
  limit?: number
}

function buildParams(filters: DealFilters): string {
  const params = new URLSearchParams()
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== "") {
      params.set(key, String(value))
    }
  })
  return params.toString()
}

export function useDeals(filters: DealFilters = {}) {
  return useQuery({
    queryKey: ["deals", filters],
    queryFn: () => {
      const qs = buildParams(filters)
      return api.get<PaginatedResponse<Deal>>(`/api/v1/deals${qs ? `?${qs}` : ""}`)
    },
  })
}

export function useDeal(id?: string) {
  return useQuery({
    queryKey: ["deals", id],
    queryFn: () => api.get<{ data: DealDetail }>(`/api/v1/deals/${id}`),
    enabled: !!id,
  })
}

export function useDealsBoard(dealTypeId?: string) {
  return useQuery({
    queryKey: ["deals_board", dealTypeId],
    queryFn: () => {
      const qs = dealTypeId ? `?deal_type_id=${dealTypeId}` : ""
      return api.get<{ data: DealBoardColumn[]; deal_type: { id: string; name: string } }>(
        `/api/v1/deals/board${qs}`
      )
    },
  })
}

export function useCreateDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (data: Record<string, unknown>) =>
      api.post<{ data: DealDetail }>("/api/v1/deals", data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
      qc.invalidateQueries({ queryKey: ["deals_board"] })
    },
  })
}

export function useUpdateDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) =>
      api.patch<{ data: DealDetail }>(`/api/v1/deals/${id}`, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
      qc.invalidateQueries({ queryKey: ["deals_board"] })
    },
  })
}

export function useDeleteDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/api/v1/deals/${id}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
      qc.invalidateQueries({ queryKey: ["deals_board"] })
    },
  })
}

export function useValidatePhaseGate() {
  return useMutation({
    mutationFn: ({ id, phase_id }: { id: string; phase_id: string }) =>
      api.post<{ valid: boolean; missing_fields: string[]; phase: { id: string; name: string } }>(
        `/api/v1/deals/${id}/validate_gate`,
        { phase_id },
      ),
  })
}

export function useChangeDealPhase() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, phase_id, loss_reason_id }: { id: string; phase_id: string; loss_reason_id?: string }) =>
      api.post<{ data: DealDetail }>(`/api/v1/deals/${id}/change_phase`, { phase_id, loss_reason_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
      qc.invalidateQueries({ queryKey: ["deals_board"] })
    },
  })
}

export function useWinDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.post<{ data: DealDetail }>(`/api/v1/deals/${id}/win`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
      qc.invalidateQueries({ queryKey: ["deals_board"] })
    },
  })
}

export function useLoseDeal() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, loss_reason_id }: { id: string; loss_reason_id?: string }) =>
      api.post<{ data: DealDetail }>(`/api/v1/deals/${id}/lose`, { loss_reason_id }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
      qc.invalidateQueries({ queryKey: ["deals_board"] })
    },
  })
}

export function useUpdateDealProducts() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, products }: { id: string; products: Record<string, unknown>[] }) =>
      api.put(`/api/v1/deals/${id}/products`, { products }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
    },
  })
}

export function useAddDealContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dealId, contact_id, role, is_primary }: { dealId: string; contact_id: string; role?: string; is_primary?: boolean }) =>
      api.post(`/api/v1/deals/${dealId}/contacts`, { contact_id, role, is_primary }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
    },
  })
}

export function useRemoveDealContact() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ dealId, contactId }: { dealId: string; contactId: string }) =>
      api.delete(`/api/v1/deals/${dealId}/contacts/${contactId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["deals"] })
    },
  })
}
